import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { BookOpen, Video, FileText, Award, ChevronRight, Clock, Users, Star } from 'lucide-react';

// Demo academy courses
const demoCourses = [
  {
    id: 'course-1',
    title: 'Introduction to Web3',
    description: 'Learn the fundamentals of Web3, blockchain technology, and decentralized applications.',
    image: 'https://images.unsplash.com/photo-1639322537228-f710d846310a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1332&q=80',
    level: 'Beginner',
    duration: '3 hours',
    modules: 5,
    students: 1245,
    rating: 4.8,
    instructor: 'Alex Johnson',
    category: 'blockchain'
  },
  {
    id: 'course-2',
    title: 'NFT Creation Masterclass',
    description: 'A comprehensive guide to creating, minting, and selling your own NFT collections.',
    image: 'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80',
    level: 'Intermediate',
    duration: '5 hours',
    modules: 8,
    students: 876,
    rating: 4.7,
    instructor: 'Sarah Williams',
    category: 'nft'
  },
  {
    id: 'course-3',
    title: 'Decentralized Finance (DeFi) Explained',
    description: 'Understand the principles of DeFi and how it is revolutionizing traditional finance.',
    image: 'https://images.unsplash.com/photo-1605792657660-596af9009e82?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1102&q=80',
    level: 'Advanced',
    duration: '6 hours',
    modules: 10,
    students: 543,
    rating: 4.9,
    instructor: 'Michael Chen',
    category: 'defi'
  },
  {
    id: 'course-4',
    title: 'Smart Contract Development',
    description: 'Learn to write, test, and deploy secure smart contracts on Ethereum and other blockchains.',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1034&q=80',
    level: 'Advanced',
    duration: '8 hours',
    modules: 12,
    students: 789,
    rating: 4.6,
    instructor: 'David Kim',
    category: 'development'
  },
  {
    id: 'course-5',
    title: 'Crypto Trading Fundamentals',
    description: 'Master the basics of cryptocurrency trading, analysis, and risk management.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    level: 'Beginner',
    duration: '4 hours',
    modules: 6,
    students: 1567,
    rating: 4.5,
    instructor: 'Emma Rodriguez',
    category: 'trading'
  },
  {
    id: 'course-6',
    title: 'Web3 Content Creation',
    description: 'Learn how to create, monetize, and distribute content in the Web3 ecosystem.',
    image: 'https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    level: 'Intermediate',
    duration: '5 hours',
    modules: 7,
    students: 632,
    rating: 4.7,
    instructor: 'James Wilson',
    category: 'content'
  }
];

// Demo articles
const demoArticles = [
  {
    id: 'article-1',
    title: 'The Future of Decentralized Streaming',
    description: 'Exploring how blockchain technology is transforming the streaming industry.',
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80',
    author: 'Alex Johnson',
    date: '2023-09-15',
    readTime: '8 min',
    category: 'streaming'
  },
  {
    id: 'article-2',
    title: 'NFTs: Beyond Digital Art',
    description: 'Discover the expanding use cases of NFTs in various industries.',
    image: 'https://images.unsplash.com/photo-1645378999013-95abebf5f3c1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    author: 'Sarah Williams',
    date: '2023-09-10',
    readTime: '6 min',
    category: 'nft'
  },
  {
    id: 'article-3',
    title: 'Web3 Security Best Practices',
    description: 'Essential security tips for navigating the Web3 ecosystem safely.',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    author: 'Michael Chen',
    date: '2023-09-05',
    readTime: '10 min',
    category: 'security'
  },
  {
    id: 'article-4',
    title: 'Creator Economy in Web3',
    description: 'How blockchain is empowering creators with new monetization models.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    author: 'Emma Rodriguez',
    date: '2023-08-28',
    readTime: '7 min',
    category: 'creator'
  }
];

const Academy: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  
  const filteredCourses = activeCategory === 'all' 
    ? demoCourses 
    : demoCourses.filter(course => course.category === activeCategory);
  
  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">FTTF Academy</h1>
        <p className="text-xl text-gray-400">
          Learn, grow, and master Web3 skills with our comprehensive courses and resources
        </p>
      </div>
      
      <Tabs defaultValue="courses" className="mb-16">
        <div className="flex justify-center mb-8">
          <TabsList>
            <TabsTrigger value="courses">
              <BookOpen className="h-4 w-4 mr-2" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="articles">
              <FileText className="h-4 w-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="tutorials">
              <Video className="h-4 w-4 mr-2" />
              Video Tutorials
            </TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="courses">
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 justify-center">
              <Button 
                variant={activeCategory === 'all' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('all')}
              >
                All
              </Button>
              <Button 
                variant={activeCategory === 'blockchain' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('blockchain')}
              >
                Blockchain
              </Button>
              <Button 
                variant={activeCategory === 'nft' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('nft')}
              >
                NFTs
              </Button>
              <Button 
                variant={activeCategory === 'defi' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('defi')}
              >
                DeFi
              </Button>
              <Button 
                variant={activeCategory === 'development' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('development')}
              >
                Development
              </Button>
              <Button 
                variant={activeCategory === 'content' ? 'default' : 'outline'} 
                onClick={() => setActiveCategory('content')}
              >
                Content Creation
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCourses.map(course => (
              <Card key={course.id} className="overflow-hidden">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={course.image} 
                    alt={course.title} 
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{course.title}</CardTitle>
                    <span className="badge-primary">{course.level}</span>
                  </div>
                  <CardDescription>{course.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between text-sm text-muted-foreground mb-4">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {course.duration}
                    </div>
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-1" />
                      {course.modules} modules
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {course.students}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary-600/20 mr-2"></div>
                    <span className="text-sm font-medium">{course.instructor}</span>
                    <div className="ml-auto flex items-center">
                      <Star className="h-4 w-4 text-yellow-500 mr-1 fill-yellow-500" />
                      <span className="text-sm font-medium">{course.rating}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button className="w-full">
                    Enroll Now
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </TabsContent>
        
        <TabsContent value="articles">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {demoArticles.map(article => (
              <Card key={article.id} className="flex flex-col md:flex-row overflow-hidden">
                <div className="md:w-1/3 aspect-video md:aspect-auto">
                  <img 
                    src={article.image} 
                    alt={article.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="md:w-2/3 flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-lg">{article.title}</CardTitle>
                    <CardDescription>{article.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <div className="flex items-center text-sm text-muted-foreground">
                      <span>{article.author}</span>
                      <span className="mx-2">•</span>
                      <span>{article.date}</span>
                      <span className="mx-2">•</span>
                      <span>{article.readTime} read</span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" className="w-full">
                      Read Article
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardFooter>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Button variant="outline" size="lg">
              View All Articles
            </Button>
          </div>
        </TabsContent>
        
        <TabsContent value="tutorials">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-video relative group">
                  <img 
                    src={`https://picsum.photos/seed/tutorial${i}/640/360`} 
                    alt={`Tutorial ${i}`} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="icon" variant="secondary" className="h-12 w-12 rounded-full">
                      <Video className="h-6 w-6" />
                    </Button>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">Video Tutorial #{i}</CardTitle>
                  <CardDescription>Learn how to {i % 2 === 0 ? 'create NFTs' : 'set up a Web3 wallet'} in this step-by-step tutorial.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {(i % 3) + 5} min
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {i * 123} views
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button variant="outline" className="w-full">
                    Watch Tutorial
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Button variant="outline" size="lg">
              View All Tutorials
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      
      <div className="bg-dark-800 rounded-2xl p-8 text-center">
        <div className="max-w-3xl mx-auto">
          <Award className="h-12 w-12 text-primary-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Become a Certified Web3 Professional</h2>
          <p className="text-xl text-gray-400 mb-6">
            Complete our comprehensive courses and earn verifiable credentials as NFTs
          </p>
          <Button size="lg">
            View Certification Programs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Academy;